import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { ChampionshipApiService } from '../../services/championship-api/ChampionshipApiService';
import { TeamApiService } from '../../services/team-api/TeamApiService';
import {
  Championship,
  Team,
} from '../../types/Event';
import { resolveTeamLogoBackground } from '../../utils/resolveTeamLogoUrl';

type TeamSortField = 'name' | 'championship';

@Component({
  selector: 'app-team-directory-page',
  standalone: false,
  templateUrl: './team-directory-page.component.html',
  styleUrl: './team-directory-page.component.sass',
})
export class TeamDirectoryPageComponent implements OnInit {
  public teams: Team[] = [];
  public championships: Championship[] = [];
  public sortField: TeamSortField = 'championship';
  public sortDirection: 'asc' | 'desc' = 'asc';
  public loading = false;
  public errorMessage: string | null = null;

  public constructor(
    private teamApi: TeamApiService,
    private championshipApi: ChampionshipApiService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await Promise.all([
      this.loadTeams(),
      this.loadChampionships(),
    ]);
    this.changeDetector.detectChanges();
  }

  public getLogoBackground(team: Team): string | null {
    return resolveTeamLogoBackground(team.logoUrl);
  }

  public getChampionshipLogo(team: Team): string | null {
    const championship = this.championships.find((item) => item.id === team.championshipId);
    return championship ? resolveTeamLogoBackground(championship.logoUrl) : null;
  }

  public setSort(field: TeamSortField): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.sortField = field;
    this.sortDirection = 'asc';
  }

  public get sortedTeams(): Team[] {
    const factor = this.sortDirection === 'asc' ? 1 : -1;
    return [
      ...this.teams,
    ].sort((left, right) => {
      if (this.sortField === 'championship') {
        const championshipSort = left.championship.localeCompare(right.championship, 'ru') * factor;
        return championshipSort || left.name.localeCompare(right.name, 'ru') * factor;
      }

      return left.name.localeCompare(right.name, 'ru') * factor;
    });
  }

  public sortState(field: TeamSortField): string {
    if (this.sortField !== field) {
      return 'none';
    }

    return this.sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  private async loadTeams(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;

    try {
      const response = await this.teamApi.getTeams();
      const teams = this.unwrapResponse(response);
      if (teams) {
        this.teams = teams;
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private async loadChampionships(): Promise<void> {
    try {
      const response = await this.championshipApi.getChampionships();
      this.championships = this.unwrapResponse(response) ?? [];
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }
}
